import { useEffect, useRef, useState } from "react";
import { useRouter, type Route } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { trackVisitEnd } from "../../telemetry/activity";
import { Button } from "../ui/Button";
import { idleGuardActive, idlePhase, type IdlePhase } from "./idleGuardRules";

/** 단계 재계산 주기 — 1초 카운트다운이 끊기지 않게 절반 */
const TICK_MS = 500;

/**
 * 전역 무동작 가드 — 완료 전 화면(`idleGuardActive`)에서 120초 입력이 없으면 "아직 계신가요?" 카운트다운(30초)을 띄우고,
 * 그래도 없으면 `visit_end{reason:"idle"}` 를 남기고 처음으로. 어떤 터치·키 입력이든 타이머를 되감고 안내를 닫는다
 * (안내의 버튼도 pointerdown 단계에서 먼저 되감긴다). 화면이 바뀌면 타이머도 새로 시작.
 * 오버레이는 fixed — 앱 루트는 stacking context 를 만들지 않으므로 카메라 <video>(fixed, z 1) 위에 올라온다.
 */
export function IdleGuard({ route }: { route: Route | null }) {
  const resetSession = useRouter((s) => s.resetSession);
  const { t } = useI18n();
  const active = route !== null && idleGuardActive(route);
  const [phase, setPhase] = useState<IdlePhase>({ kind: "active" });
  const lastInputAt = useRef(Date.now());

  useEffect(() => {
    if (!active) {
      setPhase({ kind: "active" });
      return;
    }
    lastInputAt.current = Date.now();
    const bump = () => {
      lastInputAt.current = Date.now();
      setPhase((p) => (p.kind === "active" ? p : { kind: "active" }));
    };
    const tick = () => {
      const next = idlePhase(Date.now() - lastInputAt.current);
      if (next.kind === "expired") {
        trackVisitEnd("idle");
        resetSession();
        return;
      }
      setPhase((prev) => {
        if (prev.kind === next.kind && (next.kind !== "warning" || prev.kind !== "warning" || prev.remainingSec === next.remainingSec)) return prev;
        return next;
      });
    };
    const id = window.setInterval(tick, TICK_MS);
    window.addEventListener("pointerdown", bump, { capture: true, passive: true });
    window.addEventListener("keydown", bump, { capture: true, passive: true });
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pointerdown", bump, { capture: true });
      window.removeEventListener("keydown", bump, { capture: true });
    };
  }, [active, route, resetSession]);

  if (phase.kind !== "warning") return null;

  const home = () => {
    trackVisitEnd("home");
    resetSession();
  };

  return (
    <div className="dialog" role="alertdialog" aria-live="assertive" aria-labelledby="idle-title">
      <div className="dialog__card">
        <strong className="dialog__count" aria-hidden>{phase.remainingSec}</strong>
        <h2 id="idle-title" className="dialog__title">{t.idle.title}</h2>
        <p className="dialog__body">{t.idle.body(phase.remainingSec)}</p>
        <div className="actions actions--pair dialog__actions">
          <Button variant="secondary" onClick={home}>{t.common.home}</Button>
          {/* 누르는 순간 pointerdown 이 타이머를 되감아 안내가 닫힌다 — 별도 처리 없음 */}
          <Button onClick={() => undefined}>{t.common.keepGoing}</Button>
        </div>
      </div>
    </div>
  );
}
